"use client";
import { getAvailableAppointmentTimesAction } from "@/app/actions";
import { useCurrency } from "@/app/providers/currencyContext";
import ArrowRightIcon from "@/components/_icons/arrowRightIcon";
import { ServiceSummaryErrorIcon } from "@/components/_icons/serviceSummaryErrorIcon";
import { TrashIcon } from "@/components/_icons/trashIcon";
import { CartDrawerLayout } from "@/components/cartDrawer/cartDrawerLayout";
import DefaultLogoIcon from "@/components/_icons/defaultLogoIcon";
import { ServiceTimeSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { ServiceCart } from "@/models/cart/serviceCart";
import { ServiceCartItem } from "@/models/cart/types";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import pom from "@/utils/pom";
import { LocalStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { useFormatDuration } from "@/utils/useFormatDuration";
import useLocalStorage from "@/utils/useLocalStorage";
import useMediaQuery from "@/utils/useMediaQuery";
import { DateTime, Duration } from "luxon";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import React, {
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { AvailableTimeSlots } from "./availableTimeSlots";
import Link from "next/link";
import { DEFAULT_ID, ROUTE_SERVICES } from "@/utils/constants";
import { ShortDay, shortDays, daysOfWeek } from "@/constants/enum";
import { DatePicker } from "@clover-web-lib-sprout/date-picker";
import { setCurrentLocale as setSproutCurrentLocale } from "@clover-web-lib-sprout/common";
import { saveToLocalStorage } from "@/utils/localStorageUtils";
import { checkIsHomePage } from "@/utils/nativeBridgeUtils";
import { useAuth } from "@/app/providers/authProvider";
import clsx from "clsx";
import { hasEmptyBusinessHours } from "@/utils/merchantUtils";

export const ServiceSummary = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  merchantPreferences,
  merchantColor,
}: any) => {
  const formatDuration = useFormatDuration();
  const { setIsHandlingCartDrawer, setIsProceedingToCheckout } =
    useCartDrawer();

  const router = useRouter();
  const pathname = usePathname();
  const { isLoggedIn } = useAuth();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const isHomePage = checkIsHomePage(
    pathname,
    merchant.merchantMeta.selectedSetup === "Simple",
    merchant.isStandaloneGiftCardWebsite
  );

  const t = useTranslations("ServiceSummary");
  const tName = useTranslations("ServiceModal");
  const locale = useLocale();
  const datePickerWrapperRef = useRef<HTMLDivElement>(null);
  const timeSlotsRef = useRef<HTMLDivElement>(null);

  const isCheckoutPage: boolean =
    pathname?.includes("/checkout") ||
    pathname?.includes("/guest-checkout") ||
    false;

  const handleTrapEscape = (e: React.SyntheticEvent) => {
    if (e.type !== "keydown") return;

    const target = e.target as HTMLElement;
    const DATE_BUTTON_CLASS = "CalendarTableStyles_module_dateButton";

    const { key, shiftKey } = e as React.KeyboardEvent;

    // Only intercept forward Tabs on date buttons
    if (key === "Tab" && !shiftKey) {
      const isFromDate = target.className.includes(DATE_BUTTON_CLASS);

      if (isFromDate) {
        const firstTimeSlot =
          timeSlotsRef.current?.querySelector<HTMLButtonElement>("button");

        if (firstTimeSlot) {
          e.preventDefault();
          e.stopPropagation();
          firstTimeSlot.focus();
        }
      }
    }

    if (key === "Escape") {
      onRequestClose();
    }
  };

  const minAppointmentTime =
    merchantPreferences?.appointmentPref?.minTimeBeforeAppt ?? "PT1H";
  const minDuration = Duration.fromISO(minAppointmentTime);
  const allDaysEmpty =
    merchant?.businessHours?.hours &&
    Object.values(merchant?.businessHours?.hours).every(
      (day: any) => day.length === 0
    );
  const validBusinessHours =
    merchant?.businessHours?.hours !== undefined && !allDaysEmpty;

  const minTimeGreaterThanOneDay = minDuration.as("days") >= 1;
  const getNextOpenDate = (initialDate: any) => {
    if (validBusinessHours) {
      const businessHours = merchant?.businessHours?.hours;

      let date = DateTime.fromISO(initialDate);
      let dayName = daysOfWeek[date.weekday % 7];
      while (
        !businessHours?.[dayName] ||
        businessHours?.[dayName].length === 0
      ) {
        date = date.plus({ days: 1 });
        dayName = daysOfWeek[date.weekday % 7];
      }

      return date;
    } else {
      return DateTime.fromISO(initialDate);
    }
  };

  const initialDate = minTimeGreaterThanOneDay
    ? DateTime.now().plus(minDuration).toFormat("yyyy-MM-dd")
    : DateTime.now().toFormat("yyyy-MM-dd");
  const adjustedInitialDate =
    getNextOpenDate(initialDate).toFormat("yyyy-MM-dd");

  const adjustedInitialHumanReadableDate = getNextOpenDate(initialDate)
    .setLocale(locale)
    .toLocaleString({ weekday: "long", month: "long", day: "numeric" });

  const [serviceCartItems, setServiceCartItems] = useLocalStorage<
    ServiceCartItem[]
  >(LocalStorageKey.SERVICE_CART, []);

  const [showServiceLimitError, setShowServiceLimitError] =
    useState<boolean>(false);
  const maxServicesPerAppointment =
    merchantPreferences?.appointmentPref?.bookMultiServiceLimit;

  const [showSelectAppointmentDate, setShowSelectAppointmentDate] =
    useState<boolean>(false);

  const [date, setDate] = useState(adjustedInitialDate);
  const [humanReadableDate, setHumanReadableDate] = useState(
    adjustedInitialHumanReadableDate
  );

  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const { format } = useCurrency();

  const getEmptyServiceHours = () => {
    const days: ShortDay[] = shortDays;
    if (validBusinessHours) {
      const service = merchant.businessHours;

      if (!service?.hours) return [];
      const emptyDays: ShortDay[] = [];
      daysOfWeek.forEach((day, index) => {
        if (!service.hours[day] || service.hours[day].length === 0) {
          emptyDays.push(days[index]);
        }
      });
      return emptyDays;
    } else {
      return [];
    }
  };
  const emptyDays: ShortDay[] = getEmptyServiceHours();

  useEffect(() => {
    if (
      serviceCartItems &&
      serviceCartItems.length > maxServicesPerAppointment
    ) {
      setServiceCartItems((prevCartItems: any) => {
        const updatedCartItems = prevCartItems.slice(
          0,
          maxServicesPerAppointment
        );
        return updatedCartItems;
      });
      setShowServiceLimitError(true);
    }
  }, [serviceCartItems, maxServicesPerAppointment]);

  // Set locale of Sprout calendar component
  useEffect(() => {
    setSproutCurrentLocale(
      locale as Parameters<typeof setSproutCurrentLocale>[0]
    );
  }, [locale]);

  const [isTransitioning, startTransition] = useTransition();
  const [
    availableTimesState,
    getAvailableTimesAction,
    isAvailableTimesLoading,
  ] = useActionState(getAvailableAppointmentTimesAction, {
    data: null,
    error: null,
  });
  const isLoading = isTransitioning || isAvailableTimesLoading;

  const getAvailableTimes = useCallback(
    (selectedDate: string) => {
      const serviceList = serviceCartItems?.map((service: any, index: any) => ({
        serviceId: service?.value.id,
        sortOrder: index + 1,
        ...(service.value.employee.employeeUuid !== "default" && {
          employeeIds: [service.value.employee.employeeUuid],
        }),
      }));

      if (serviceList.length === 0) return;

      const selectedDt = DateTime.fromISO(selectedDate, {
        zone: merchant.timezone,
      });
      const startDate = selectedDt.toISO();
      const endDate = selectedDt.endOf("day").toISO();

      startTransition(() => {
        getAvailableTimesAction({
          mid: merchant?.id,
          serviceList: serviceList,
          startDate: startDate,
          endDate: endDate,
        });
      });
    },
    [serviceCartItems, merchant]
  );

  useEffect(() => {
    const shouldFetch =
      (isCheckoutPage || showSelectAppointmentDate) &&
      !availableTimesState.data &&
      !isLoading;

    if (shouldFetch) {
      getAvailableTimes(date);
    }
  }, [
    isCheckoutPage,
    showSelectAppointmentDate,
    availableTimesState.data,
    isLoading,
    date,
  ]);

  const removeFromCart = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    const itemToRemove = serviceCartItems[index];
    if (itemToRemove) {
      ServiceCart.removeItem(itemToRemove.key);
    }
  };

  const handleContinueToCheckout = () => {
    const dateTimeString = `${date}T${selectedTime}:00`;
    const dateTimeWithTimezone = DateTime.fromISO(dateTimeString, {
      zone: merchant.timezone,
    });
    const epochTimeInMilliseconds = dateTimeWithTimezone.toMillis();
    const updatedServiceCartItems = serviceCartItems?.map((item: any) => ({
      ...item,
      value: {
        ...item?.value,
        appointmentTime: epochTimeInMilliseconds,
      },
    }));
    setServiceCartItems(updatedServiceCartItems);
    setIsHandlingCartDrawer(false);
    setIsProceedingToCheckout(true);
    if (isLoggedIn) {
      router.push("/checkout");
      onRequestClose();
    } else {
      setShowSelectAppointmentDate(false);
      onRequestClose();
      onTriggerLoginModal(true);
    }
  };

  if (hasEmptyBusinessHours(merchant)) {
    return <></>;
  }

  const handleSelectDate = (selectedDate: string) => {
    setDate(selectedDate);
    setHumanReadableDate(
      DateTime.fromISO(selectedDate).setLocale(locale).toLocaleString({
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    );
    setSelectedTime(null);
    getAvailableTimes(selectedDate);
  };

  const timeslots =
    availableTimesState?.data?.totalAvailableTimeslots?.[0]?.timeslots;

  const handleSelectTime = (time: string) => {
    setSelectedTime(time);
  };

  const maxAppointmentTime =
    merchantPreferences?.appointmentPref?.maxTimeBeforeAppt ?? "P30D";

  const minDaysToAdd = minDuration.days >= 1 ? minDuration.days : 0;

  const nowMinusOneDay = DateTime.now().minus({ days: 1 });
  const startTimestamp = nowMinusOneDay.plus({ days: minDaysToAdd }).toMillis();
  const endTimestamp = nowMinusOneDay
    .plus({ days: Duration.fromISO(maxAppointmentTime).days })
    .toMillis();

  const dateBoundaries = {
    startTimestamp,
    endTimestamp,
  };

  const handleServiceItemClick = () => {
    setIsHandlingCartDrawer(false);
    setIsProceedingToCheckout(true);
    onRequestClose();
  };

  const handleEditTime = () => {
    const dateTimeString = `${date}T${selectedTime}:00`;
    const dateTimeWithTimezone = DateTime.fromISO(dateTimeString, {
      zone: merchant.timezone,
    });
    const epochTimeInMilliseconds = dateTimeWithTimezone.toMillis();
    const updatedServiceCartItems = serviceCartItems?.map((item: any) => ({
      ...item,
      value: {
        ...item?.value,
        appointmentTime: epochTimeInMilliseconds,
      },
    }));
    setServiceCartItems(updatedServiceCartItems);
    /**
     * There is a time stamp for cart items in local storage being compared to refresh checkout header on apppointment booking time change
     * On updating service cart, updating the version key as well
     */
    const versionKey = LocalStorageKey.SERVICE_CART_TIMESTAMP;
    const newVersion = Date.now();
    saveToLocalStorage(versionKey, newVersion);
    setIsHandlingCartDrawer(false);
    setIsProceedingToCheckout(true);
    onRequestClose();
  };

  const renderEmployeeName = (chunks: React.ReactNode) => (
    <span style={{ color: merchantColor }}>{chunks}</span>
  );

  const isServiceUnavailable = serviceCartItems?.length === 0 || selectedTime === null;

  if (!showSelectAppointmentDate) {
    return (
      <CartDrawerLayout
        isOpen={isOpen}
        isHomePage={isHomePage}
        onClose={onClose}
        onRequestClose={() => {
          setShowSelectAppointmentDate(false);
          onRequestClose();
        }}
        title={t("appointmentSummary")}
        testId={applyDataTestId(pom.services.serviceSummary)}
        contentClassName="p-6"
        closeCartLabel={t("closeCart")}
        hasItems={serviceCartItems.length > 0}
        headerContent={
          <>
            <div className="flex flex-row pl-4 items-center pt-6 sm:pt-0">
              {merchant?.logo ? (
                <Image
                  className="h-[50px]"
                  src={merchant.logo}
                  alt={pom.header.merchantLogo}
                  width={50}
                  height={50}
                />
              ) : (
                <DefaultLogoIcon
                  className="h-[50px] w-[50px]"
                  aria-label={pom.header.merchantLogo}
                />
              )}
              <div className="flex flex-col px-4">
                <p className="font-medium text-[24px]">{merchant.name}</p>
                <p className="text-[15px]">
                  {merchant.address?.address1 +
                    merchant.address?.address2 +
                    ", " +
                    merchant.address?.city +
                    ", " +
                    merchant.address?.state +
                    ", " +
                    merchant.address?.zip}
                </p>
              </div>
            </div>
            {showServiceLimitError && (
              <div className="w-full">
                <div className="mx-5 mt-5 h-[72px] bg-[#FFEDED] rounded-lg">
                  <div className="flex flex-row h-full items-center justify-center">
                    <div className="mx-4">
                      <ServiceSummaryErrorIcon />
                    </div>
                    <p>
                      {t("serviceLimitMessage", {
                        count: maxServicesPerAppointment,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        }
        footerContent={
          <button
            data-testid={applyDataTestId(
              pom.services.scheduleAppointmentButton
            )}
            style={{
              backgroundColor:
                serviceCartItems?.length == 0 ? "#C7C7C7" : undefined,
            }}
            className="bg-black text-white font-bold py-2 px-[18px] clover-button-primary sm:px-0 h-16 sm:rounded-md w-full sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center"
            onClick={() => {
              setShowSelectAppointmentDate(true);
            }}
            disabled={serviceCartItems?.length === 0}
          >
            <span className="text-xl font-medium">
              {t("scheduleAppointment")}
            </span>
            {isMobileScreen && (
              <ArrowRightIcon
                className="sm:hidden"
                color="currentColor"
                width={24}
                height={24.7}
              />
            )}
          </button>
        }
      >
        <div data-testid={applyDataTestId(pom.services.serviceSummaryCart)}>
          {serviceCartItems?.map((item, index: number) => {
            const { value } = item;
            const nameToUse =
              value.employee.employeeUuid === DEFAULT_ID
                ? tName("anyAvailableStaff")
                : value.employee.nickName || value.employee.name;

            return (
              <div
                key={item.key}
                className="w-full flex flex-row justify-between items-center py-4 border-b cursor-pointer"
              >
                <Link
                  className="w-full flex flex-col justify-between items-start cursor-pointer"
                  href={`${ROUTE_SERVICES}/${getItemUrl(item.value)}`}
                  onClick={handleServiceItemClick}
                  aria-label={t("editItem", { itemName: value.name })}
                >
                  <p className="text-lg text-[#373737]">{value.name}</p>
                  <div>
                    {value?.serviceDetails?.priceType === "PER_UNIT" && (
                      <p className="text-[15px] text-[#727272]">
                        {t("serviceCartItemPerUnit", {
                          quantity: value.quantity,
                          price: format(value.serviceDetails.price),
                          unitName: value.serviceDetails.unitName ?? "",
                        })}
                      </p>
                    )}
                    {value.modifiers.length > 0 && (
                      <p className="text-[15px] text-[#727272]">
                        {modifierDisplay(value.modifiers)}
                      </p>
                    )}
                    <div className="w-full">
                      <p className="text-[15px] text-[#727272]">
                        {t.rich("withEmployee", {
                          employeeName: renderEmployeeName,
                          name: nameToUse,
                        })}
                      </p>
                    </div>
                    <div className="w-full flex flex-row justify-start">
                      <p className="text-[15px] text-[#727272]">
                        {value?.serviceDetails?.serviceItem &&
                          formatDuration(
                            value.serviceDetails.serviceItem.duration
                          )}
                      </p>
                    </div>
                  </div>
                </Link>
                <div className="flex flex-col justify-center items-end">
                  <p className="text-[16px] font-medium mt-0.5">
                    {format(item?.value?.total)}
                  </p>
                  <button
                    onClick={(event: React.MouseEvent) =>
                      removeFromCart(event, index)
                    }
                    aria-label={t("removeItem", { itemName: value.name })}
                    className="mt-2"
                  >
                    <TrashIcon width={14} height={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </CartDrawerLayout>
    );
  } else {
    return (
      <CartDrawerLayout
        isOpen={isOpen}
        isHomePage={isHomePage}
        onClose={() => {
          setShowSelectAppointmentDate(false);
          onRequestClose();
        }}
        onRequestClose={() => {
          setShowSelectAppointmentDate(false);
          onRequestClose();
        }}
        title={t("selectDate")}
        testId={applyDataTestId(pom.services.serviceSummary)}
        contentClassName="p-6"
        hasItems={true}
        headerContent={
          <style>{`
            button[class*='CalendarTableStyles_module_selected'] {
              background-color: ${merchantColor}!important;
              box-shadow: none!important;
            }
          `}</style>
        }
        footerContent={
          <>
            {!isCheckoutPage && (
              <button
                data-testid={applyDataTestId(
                  pom.services.serviceSummaryContinue
                )}
                style={{
                  backgroundColor: isServiceUnavailable ? "#C7C7C7" : undefined,
                }}
                className={clsx(
                  "bg-black text-white font-bold py-2 px-[18px] sm:px-0 sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] sm:justify-center items-center justify-between flex",
                  {
                    "clover-button-primary": !isServiceUnavailable,
                  }
                )}
                onClick={handleContinueToCheckout}
                disabled={isServiceUnavailable}
                aria-label={t("continueToCheckout")}
              >
                <span className="text-xl font-medium">
                  {t("continueToCheckout")}
                </span>
                {isMobileScreen && (
                  <ArrowRightIcon
                    className="sm:hidden"
                    color="currentColor"
                    width={24}
                    height={24.7}
                  />
                )}
              </button>
            )}
            {isCheckoutPage && (
              <button
                data-testid={applyDataTestId(
                  pom.services.serviceSummaryContinue
                )}
                style={{
                  backgroundColor:
                    serviceCartItems?.length == 0 || selectedTime == null
                      ? "#C7C7C7"
                      : merchantColor,
                }}
                className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
                onClick={handleEditTime}
                disabled={serviceCartItems?.length == 0 || selectedTime == null}
                aria-label={t("confirm")}
              >
                <p className="text-xl font-medium">{t("confirm")}</p>
              </button>
            )}
          </>
        }
      >
        <div data-testid={applyDataTestId(pom.services.serviceSummaryCart)}>
          <section
            ref={datePickerWrapperRef}
            onKeyDownCapture={handleTrapEscape}
            aria-label={t("selectDate")}
          >
            <DatePicker
              value={date}
              label=""
              className="services_overrides_storefront"
              dateBoundaries={dateBoundaries}
              disableRecurringDays={emptyDays}
              disablePastDates
              enableFutureDates
              onDate={handleSelectDate}
              isModal={!isMobileScreen}
              isExposed={isMobileScreen}
              summaryHour={0}
              timezone={merchant.timezone}
            />
          </section>
          <div ref={timeSlotsRef}>
            <h3 className="font-medium text-base pb-4">
              {t("availabilityFor", { date: humanReadableDate })}
            </h3>
            {isLoading && <ServiceTimeSkeleton />}
            {!isLoading && (
              <AvailableTimeSlots
                availableTimeSlots={timeslots}
                selectedTime={selectedTime}
                handleSelectedTime={handleSelectTime}
                merchantColor={merchantColor}
              />
            )}
          </div>
        </div>
      </CartDrawerLayout>
    );
  }
};
